import React, { useEffect, useState } from 'react';
import { bool, arrayOf, number, shape } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { FormattedMessage, injectIntl, intlShape, useIntl } from '../../util/reactIntl';
import {
  REVIEW_TYPE_OF_PROVIDER,
  REVIEW_TYPE_OF_CUSTOMER,
  SCHEMA_TYPE_MULTI_ENUM,
  SCHEMA_TYPE_TEXT,
  propTypes,
} from '../../util/types';
import { ensureCurrentUser, ensureUser, isListingFavorite } from '../../util/data';
import { withViewport } from '../../util/uiHelpers';
import { pickCustomFieldProps } from '../../util/fieldHelpers';
import { richText } from '../../util/richText';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart } from '@fortawesome/free-regular-svg-icons';
import { faHeart as faHeartSolid } from '@fortawesome/free-solid-svg-icons';

import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  Heading,
  H2,
  H4,
  Page,
  AvatarLarge,
  NamedLink,
  ListingCard,
  Reviews,
  ButtonTabNavHorizontal,
  LayoutSideNavigation,
  LayoutSingleColumn,
} from '../../components';

import TopbarContainer from '../../containers/TopbarContainer/TopbarContainer';
import FooterContainer from '../../containers/FooterContainer/FooterContainer';
import NotFoundPage from '../../containers/NotFoundPage/NotFoundPage';

import css from './ProfilePage.module.css';
import SectionDetailsMaybe from './SectionDetailsMaybe';
import SectionTextMaybe from './SectionTextMaybe';
import SectionMultiEnumMaybe from './SectionMultiEnumMaybe';
import { useHistory } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { createResourceLocatorString } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useUpdateProfileMutation } from '../../ducks/UserApi';
import { createSlug } from '../../util/urlHelpers';
import getCountryCodes, { countryCodes } from '../../translations/countryCodes';

const MAX_MOBILE_SCREEN_WIDTH = 768;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

export const AsideContent = props => {
  const { user, displayName, isCurrentUser } = props;
  return (
    <div className={css.asideContent}>
      <AvatarLarge className={css.avatar} user={user} disableProfileLink />
      <H2 as="h1" className={css.mobileHeading}>
        {displayName ? (
          <FormattedMessage id="ProfilePage.mobileHeading" values={{ name: displayName }} />
        ) : null}
      </H2>
      {isCurrentUser ? (
        <>
          <NamedLink className={css.editLinkMobile} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkMobile" />
          </NamedLink>
          <NamedLink className={css.editLinkDesktop} name="ProfileSettingsPage">
            <FormattedMessage id="ProfilePage.editProfileLinkDesktop" />
          </NamedLink>
        </>
      ) : null}
    </div>
  );
};

export const ReviewsErrorMaybe = props => {
  const { queryReviewsError } = props;
  return queryReviewsError ? (
    <p className={css.error}>
      <FormattedMessage id="ProfilePage.loadingReviewsFailed" />
    </p>
  ) : null;
};

export const MobileReviews = props => {
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  return (
    <div className={css.mobileReviews}>
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsFromMyCustomersTitle"
          values={{ count: reviewsOfProvider.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfProvider} />
      <H4 as="h2" className={css.mobileReviewsTitle}>
        <FormattedMessage
          id="ProfilePage.reviewsAsACustomerTitle"
          values={{ count: reviewsOfCustomer.length }}
        />
      </H4>
      <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />
      <Reviews reviews={reviewsOfCustomer} />
    </div>
  );
};

export const DesktopReviews = props => {
  const [showReviewsType, setShowReviewsType] = useState(REVIEW_TYPE_OF_PROVIDER);
  const { reviews, queryReviewsError } = props;
  const reviewsOfProvider = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_PROVIDER);
  const reviewsOfCustomer = reviews.filter(r => r.attributes.type === REVIEW_TYPE_OF_CUSTOMER);
  const isReviewTypeProviderSelected = showReviewsType === REVIEW_TYPE_OF_PROVIDER;
  const isReviewTypeCustomerSelected = showReviewsType === REVIEW_TYPE_OF_CUSTOMER;
  const desktopReviewTabs = [
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsFromMyCustomersTitle"
            values={{ count: reviewsOfProvider.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeProviderSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_PROVIDER),
    },
    {
      text: (
        <Heading as="h3" rootClassName={css.desktopReviewsTitle}>
          <FormattedMessage
            id="ProfilePage.reviewsAsACustomerTitle"
            values={{ count: reviewsOfCustomer.length }}
          />
        </Heading>
      ),
      selected: isReviewTypeCustomerSelected,
      onClick: () => setShowReviewsType(REVIEW_TYPE_OF_CUSTOMER),
    },
  ];

  return (
    <div className={css.desktopReviews}>
      <div className={css.desktopReviewsWrapper}>
        <ButtonTabNavHorizontal className={css.desktopReviewsTabNav} tabs={desktopReviewTabs} />

        <ReviewsErrorMaybe queryReviewsError={queryReviewsError} />

        {isReviewTypeProviderSelected ? (
          <Reviews reviews={reviewsOfProvider} />
        ) : (
          <Reviews reviews={reviewsOfCustomer} />
        )}
      </div>
    </div>
  );
};

export const CustomUserFields = props => {
  const { publicData, metadata, userFieldConfig } = props;

  const shouldPickUserField = fieldConfig => fieldConfig?.showConfig?.displayInProfile !== false;
  const propsForCustomFields =
    pickCustomFieldProps(publicData, metadata, userFieldConfig, 'userType', shouldPickUserField) ||
    [];

  return (
    <>
      <SectionDetailsMaybe {...props} />
      {propsForCustomFields.map(customFieldProps => {
        const { schemaType, ...fieldProps } = customFieldProps;
        return schemaType === SCHEMA_TYPE_MULTI_ENUM ? (
          <SectionMultiEnumMaybe {...fieldProps} />
        ) : schemaType === SCHEMA_TYPE_TEXT ? (
          <SectionTextMaybe {...fieldProps} />
        ) : null;
      })}
    </>
  );
};

export const MainContent = props => {
  const routes = useRouteConfiguration();
  const history = useHistory();
  const intl = useIntl()
  const [
    updateProfile,
    { isLoading: updateInProgress, error: updateError },
  ] = useUpdateProfileMutation();
  const {
    userShowError,
    bio,
    displayName,
    listings,
    queryListingsError,
    publicData,
    profileImage,
    currentUser,
    profileUser
  } = props;

  const bioWithLinks = bio?.split('\n')?.map(bioSplit =>
    richText(bioSplit, {
      linkify: true,
      longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
      longWordClass: css.longWord,
    })
  );

  if (userShowError || queryListingsError) {
    return (
      <p className={css.error}>
        <FormattedMessage id="ProfilePage.loadingDataFailed" />
      </p>
    );
  }

  const { xsmall2x, xsmall } = profileImage?.attributes?.variants ?? {};

  const update = (e, listing) => {
    e.stopPropagation();
    e.preventDefault();
    if (!currentUser?.id?.uuid) {
      history.push(
        createResourceLocatorString('SignupForUserTypePage', routes, { userType: 'lessor' }, {})
      );
      return;
    }

    const favoriteListings =
      currentUser?.attributes?.profile?.protectedData?.favoriteListings || [];
    const listingId = listing?.id?.uuid;
    let favorites;

    if (updateInProgress) {
      return;
    }
    const isFavorite = isListingFavorite({ currentUser, listing });

    if (isFavorite) {
      favorites = favoriteListings.filter(v => v !== listingId);
    } else {
      favorites = [...favoriteListings, listingId];
    }

    const updateValues = {
      protectedData: {
        favoriteListings: favorites,
      },
    };

    return updateProfile(updateValues);
  };

  const country = countryCodes.find(country => country.code === publicData?.addressCountry);

  return (
    <div className={css.mainContent}>
      <div className={css.infoSection}>
        <div className={css.infoSegment}>
          <div>
            <AvatarLarge className={profileUser.profileImage ? css.avatar : css.noAvatar} user={profileUser} />
            {/* <img
              className={css.companyLogo}
              alt="company-logo"
              src={xsmall2x?.url ?? xsmall?.url}
            /> */}
          </div>
          {bioWithLinks?.map(bioWithLink => {
            return bioWithLink?.join('') ? <p className={css.companyBio}>{bioWithLink}</p> : <></>;
          })}
        </div>
        <div>
          <div className={css.addressSegment}>
            <span className={css.addressName}>{displayName}</span>
            <span className={css.addressInfo}>
              {`${publicData?.addressStreet ?? ''} ${publicData?.addressStreetNumber ?? ''}`} <br />{' '}
              {`${publicData?.addressPostalcode ?? ''} ${publicData?.addressCity ?? ''}`} <br />{' '}
              {`${country?.[intl.locale] ?? ''}`}
            </span>
          </div>
        </div>
      </div>
      <div className={css.listingsSection}>
        <h3 className={css.listingsHeading}>{intl.formatMessage({ id: 'ProfilePage.listingsHeading' }, {displayName})}</h3>
        <div className={css.listingsSegment}>
          {listings?.map(listing => (
            <div key={listing?.attributes?.title ?? ''} className={css.listingCard}>
              <NamedLink
                name="ListingPage"
                className={css.listingWrapper}
                params={{
                  id: listing?.id?.uuid,
                  slug: createSlug(listing?.attributes?.title ?? ''),
                }}
              >
                <img
                  className={css.listingImage}
                  alt={listing?.attributes?.title}
                  src={
                    listing?.images?.[0]?.attributes?.variants?.['listing-card-2x']?.url ??
                    listing?.images?.[0]?.attributes?.variants?.['listing-card']?.url ??
                    ''
                  }
                />
              </NamedLink>
              <div className={css.listingInfoContainer}>
                <div className={css.listingInfoSegment}>
                  <NamedLink
                    name="ListingPage"
                    className={css.listingWrapper}
                    params={{
                      id: listing?.id?.uuid,
                      slug: createSlug(listing?.attributes?.title ?? ''),
                    }}
                  >
                    <div className={css.listingTitle}>{listing?.attributes?.title}</div>
                  </NamedLink>
                  <div className={css.listingDescription}>{listing?.attributes?.description}</div>
                </div>
                <FontAwesomeIcon
                  className={css.listingFavIcon}
                  onClick={e => update(e, listing)}
                  icon={isListingFavorite({ currentUser, listing }) ? faHeartSolid : faHeart}
                  spin={updateInProgress}
                />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export const ProfilePageComponent = props => {
  const history = useHistory();
  const location = useLocation();
  const config = useConfiguration();
  const { scrollingDisabled, currentUser, userShowError, user, intl, ...rest } = props;
  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const profileUser = ensureUser(user);
  const isCurrentUser =
    ensuredCurrentUser.id && profileUser.id && ensuredCurrentUser.id.uuid === profileUser.id.uuid;
  const { bio, displayName, publicData, metadata, protectedData } =
    profileUser?.attributes?.profile || {};
  const { userFields } = config.user;

  useEffect(() => {
    if (publicData && publicData?.userType !== 'lessor') {
      history.push('/', location.state);
    }
  }, [publicData]);

  const schemaTitleVars = { name: displayName, marketplaceName: config.marketplaceName };
  const schemaTitle = intl.formatMessage({ id: 'ProfilePage.schemaTitle' }, schemaTitleVars);

  if (userShowError && userShowError.status === 404) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }
  return (
    <Page
      scrollingDisabled={scrollingDisabled}
      title={schemaTitle}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'ProfilePage',
        name: schemaTitle,
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<TopbarContainer />}
        footer={<FooterContainer />}
      >
        <MainContent
          bio={bio}
          displayName={displayName}
          userShowError={userShowError}
          publicData={publicData}
          metadata={metadata}
          userFieldConfig={userFields}
          intl={intl}
          profileImage={profileUser.profileImage}
          currentUser={currentUser}
          profileUser={profileUser}
          {...rest}
        />
      </LayoutSingleColumn>
    </Page>
  );
};

ProfilePageComponent.defaultProps = {
  currentUser: null,
  user: null,
  userShowError: null,
  queryListingsError: null,
  reviews: [],
  queryReviewsError: null,
};

ProfilePageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  currentUser: propTypes.currentUser,
  user: propTypes.user,
  userShowError: propTypes.error,
  queryListingsError: propTypes.error,
  listings: arrayOf(propTypes.listing).isRequired,
  reviews: arrayOf(propTypes.review),
  queryReviewsError: propTypes.error,

  // form withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  const { currentUser } = state.user;
  const {
    userId,
    userShowError,
    queryListingsError,
    userListingRefs,
    reviews,
    queryReviewsError,
  } = state.ProfilePage;
  const userMatches = getMarketplaceEntities(state, [{ type: 'user', id: userId }]);
  const user = userMatches.length === 1 ? userMatches[0] : null;
  const listings = getMarketplaceEntities(state, userListingRefs);
  return {
    scrollingDisabled: isScrollingDisabled(state),
    currentUser,
    user,
    userShowError,
    queryListingsError,
    listings,
    reviews,
    queryReviewsError,
  };
};

const ProfilePage = compose(
  connect(mapStateToProps),
  withViewport,
  injectIntl
)(ProfilePageComponent);

export default ProfilePage;
